import React from 'react';
import { useSelectedTeamState } from 'src/hooks/useSelectedTeam';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Divider,
  Card,
  CardContent,
  Stack,
  IconButton,
  Avatar
} from '@mui/material';
import { Icon } from '@iconify/react';
import { fDateTime } from 'src/utils/formatTime';
import { useTheme, alpha } from '@mui/material/styles';

const CustomerInfoPanel = ({ customerData, isLoading }) => {
  const theme = useTheme();
  const { teamCurrencySymbol } = useSelectedTeamState();
  const isDark = theme.palette.mode === 'dark';
  const cardBackground = isDark ? alpha(theme.palette.common.white, 0.04) : theme.palette.common.white;
  const cardBorder = isDark ? alpha(theme.palette.common.white, 0.08) : 'rgba(145, 158, 171, 0.12)';
  const cardShadow = isDark
    ? '0 25px 50px rgba(2, 6, 23, 0.6)'
    : 'rgba(145, 158, 171, 0.16) 0px 4px 8px 0px, rgba(145, 158, 171, 0.12) 0px 8px 16px -4px';
  const cardHoverShadow = isDark
    ? '0 35px 65px rgba(2, 6, 23, 0.75)'
    : 'rgba(145, 158, 171, 0.24) 0px 8px 16px 0px, rgba(145, 158, 171, 0.16) 0px 16px 32px -8px';
  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading customer information...</Typography>
      </Box>
    );
  }

  if (!customerData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No customer data available</Typography>
      </Box>
    );
  }

  const InfoItem = ({ label, value, chip = false, chipColor = 'default' }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
        {label}
      </Typography>
      {chip ? (
        <Chip
          label={value}
          color={chipColor}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ) : (
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value || 'N/A'}
        </Typography>
      )}
    </Box>
  );

  const SectionCard = ({ title, icon, children }) => (
    <Card sx={{ 
      mb: 3, 
      boxShadow: cardShadow,
      border: `1px solid ${cardBorder}`,
      borderRadius: 2,
      backgroundColor: cardBackground,
      backdropFilter: isDark ? 'blur(6px)' : 'none',
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        boxShadow: cardHoverShadow,
        transform: 'translateY(-2px)'
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Icon icon={icon} width={24} height={24} style={{ marginRight: 8, color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  // Format status
  const getStatusChip = (status) => {
    const isEnabled = status === 'Y';
    return {
      label: isEnabled ? 'Active' : 'Inactive',
      color: isEnabled ? 'success' : 'error'
    };
  };

  const statusChip = getStatusChip(customerData.active);

  return (
    <Box sx={{ p: 0 }}>
      {/* Basic Information */}
      <SectionCard title="Basic Information" icon="eva:person-fill">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <InfoItem 
              label="Name" 
              value={`${customerData.firstName || ''} ${customerData.lastName || ''}`.trim()} 
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoItem label="Email" value={customerData.email} />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoItem label="Phone" value={customerData.phone} />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoItem 
              label="Status" 
              value={statusChip.label}
              chip={true}
              chipColor={statusChip.color}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoItem label="GST Number" value={customerData.gstin} />
          </Grid>
          {/* <Grid item xs={12} md={6}>
            <InfoItem label="VAT Number" value={customerData.vatNumber} />
          </Grid> */}
          <Grid item xs={12} md={6}>
            <InfoItem 
              label="Wallet Balance" 
              value={customerData.balanceAmount ? `${teamCurrencySymbol}${customerData.balanceAmount}` : 'N/A'} 
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoItem 
              label="Created On" 
              value={customerData.createdAt ? fDateTime(customerData.createdAt, 'yyyy-MM-dd hh:mm a') : 'N/A'} 
            />
          </Grid>
        </Grid>
      </SectionCard>

      {/* Company Associations */}
      {customerData.companyName && (
        <SectionCard title="Company Associations" icon="eva:briefcase-fill">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {customerData.companyName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: {customerData.companyType || 'CPO'}
              </Typography>
              {customerData.joinedDate && (
                <Typography variant="body2" color="text.secondary">
                  Joined: {fDateTime(customerData.joinedDate, 'yyyy-MM-dd')}
                </Typography>
              )}
            </Box>
            <Chip
              label="Active"
              color="success"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </SectionCard>
      )}

      {/* Team Memberships */}
      {/* {customerData.teamMemberships && customerData.teamMemberships.length > 0 && (
        <SectionCard title="Team Memberships" icon="eva:people-fill">
          {customerData.teamMemberships.map((team, index) => (
            <Box key={index}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {team.teamName || 'Operations Team'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Company: {team.companyName || customerData.companyName}
                  </Typography>
                  {team.joinedDate && (
                    <Typography variant="body2" color="text.secondary">
                      Joined: {fDateTime(team.joinedDate, 'yyyy-MM-dd')}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={team.role || 'MANAGER'}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
          ))}
        </SectionCard>
      )} */}
    </Box>
  );
};

export default CustomerInfoPanel;
